/**
 * Persists active async job metadata to localStorage so the
 * ProgressNotificationStack can survive page refreshes.
 *
 * Only jobs that received a BACKEND jobId (returned by the 202 response) are
 * persisted — those are the only ones we can poll via /async-job/{jobId}/status.
 * Client-side-only UUIDs (e.g. autoFeedActuals server-triggered cascade) are
 * NOT persisted because there's no server-side record to query.
 *
 * Session scoping (prevents stale cards bleeding across users/environments):
 *   - The storage bucket is keyed by the current USER: `offolio_ws_jobs:{username}`.
 *     `username` lives in localStorage and is available even at the unconditional,
 *     pre-auth mount of <ProgressNotificationStack/>, so it's a timing-safe scope.
 *   - The ENVIRONMENT (tenant) is NOT reliably resolvable that early (it comes from
 *     Redux `currentUser.tenant`, hydrated from GET /users/self), so each entry is
 *     instead TAGGED with `env` at write time and filtered out on read once the
 *     current env is known. Env transitions are additionally hard-cleared on logout
 *     and on tenant switch (see clearAllPersistedJobs).
 *
 * Shape:  { [jobId]: { jobId, kind, topic, startedAt, env, label?, fileName? } }
 *
 * TTL: 2 hours — jobs older than that are pruned on every read (the backend
 * AsyncJobRegistry TTL is 10 min, so older entries would return 404 anyway).
 */

import { getSessionTenant } from "../../global/sessionScope";

const STORAGE_PREFIX = "offolio_ws_jobs";
const TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

/** Current user identity — survives refresh in localStorage; cleared on user switch. */
function currentUser() {
  try {
    return localStorage.getItem("username") || "anon";
  } catch {
    return "anon";
  }
}

/**
 * Current environment (tenant name), or null when it isn't resolvable yet
 * (very early app load, before currentUser is hydrated). Callers treat null as
 * "unknown — don't filter".
 */
function currentEnv() {
  // Pushed in by the store subscriber (store/index.js) so we read the tenant
  // without importing the store — keeps this util out of the store import cycle.
  return getSessionTenant();
}

/** Per-user storage bucket key. */
function storageKey() {
  return `${STORAGE_PREFIX}:${currentUser()}`;
}

function readAll() {
  try {
    const raw = localStorage.getItem(storageKey());
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeAll(map) {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(map));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

/** Save a job when it starts (backend jobId confirmed). */
export function persistJob({ jobId, kind, topic, startedAt, label, fileName }) {
  if (!jobId || !kind) return;
  const map = readAll();
  map[jobId] = {
    jobId,
    kind,
    topic,
    startedAt: startedAt || Date.now(),
    env: currentEnv(),
    label,
    fileName,
  };
  writeAll(map);
}

/** Remove a job (on dismiss, completion or error). */
export function removePersistedJob(jobId) {
  if (!jobId) return;
  const map = readAll();
  delete map[jobId];
  writeAll(map);
}

/**
 * Return all persisted jobs younger than TTL_MS AND belonging to the current
 * environment. Prunes stale / cross-env entries as a side-effect.
 */
export function loadPersistedJobs() {
  const map = readAll();
  const cutoff = Date.now() - TTL_MS;
  const env = currentEnv();
  const fresh = {};
  const stale = [];
  for (const [id, entry] of Object.entries(map)) {
    const tooOld = (entry.startedAt || 0) < cutoff;
    // Drop entries from a different environment — but only when the current env
    // is actually known. At the earliest mount it's null; the hard clear on
    // logout / tenant switch covers env transitions regardless.
    const wrongEnv = env != null && entry.env != null && entry.env !== env;
    if (tooOld || wrongEnv) {
      stale.push(id);
    } else {
      fresh[id] = entry;
    }
  }
  if (stale.length) writeAll(fresh);
  return Object.values(fresh);
}

/**
 * Updates the persisted progress state for an active job.
 * Called by the Redux store subscriber after each meaningful frame
 * so that totalProjects, completedCount and etaSeconds survive a refresh.
 */
export function updatePersistedJobState(jobId, patch) {
  if (!jobId) return;
  const map = readAll();
  if (!map[jobId]) return; // only update entries that already exist
  map[jobId] = { ...map[jobId], ...patch };
  writeAll(map);
}

/** Called when a job is dismissed — removes from persistence. */
export function clearPersistedJob(jobId) {
  removePersistedJob(jobId);
}

/**
 * Hard clear: remove EVERY persisted-jobs bucket (every user / env on this
 * browser, plus any legacy unscoped bucket). Called on logout and on tenant
 * switch so progress cards can never bleed across sessions or environments.
 */
export function clearAllPersistedJobs() {
  try {
    Object.keys(localStorage)
      .filter((k) => k === STORAGE_PREFIX || k.startsWith(`${STORAGE_PREFIX}:`))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    // localStorage unavailable — nothing to clear
  }
}
